import styles from './ManageUser.module.scss';
import Button from '../../../common/_custom/Button/Button';
import ManageUserTable from './ManageUserTable/ManageUserTable';
import clsx from 'clsx';
import InviteTeammateModal from '../../../common/InviteTeammateModal/InviteTeammateModal';
import { plusWhite, plusWhite2, debtProdFilters, chatbotGif } from '../../../assets/hostedassets';
import CrossIcon from '../../../assets/Svg/CrossIcon';
import AddUserModal from './ManageUserTable/components/AddUserModal';
import { isNil } from 'lodash';
import { useManageUser } from './useManageUser';
import OrgBasedUsersTable, { OrgBasedUser } from './OrgBasedUsersTable/OrgBasedUsersTable';
import aicaLoader from '../../../assets/aica-loader.svg';
import SearchBox from '../Portfolio/components/CompaniesList/components/SearchBox/SearchBox';
import { MANAGE_USERS_TABS } from '../../../store/investor/constants';
import { useAppDispatch } from '../../../app/hooks';
import { setCustomFilterDrawer } from '../../../store/investor/action';
import { useIsMobile } from '../../../common/_custom/Hooks/useIsMobile';
import MobileHeader from '../../../common/MobileLayout/MobileHeader';
import OrgCardList from './OrgCard/OrgCardList';
import FilterTriggerButton from '../../../common/FilterTriggerButton/FilterTriggerButton';
import MobilePageHeader from '../../../common/MobileLayout/MobilePageHeader';
import MobileBottomTabs from '../../../common/MobileLayout/MobileBottomTabs';
import { usePortfolioAskAicaEnabled } from '../../../common/PortfolioAskAicaEntry/usePortfolioAskAicaEnabled';
import { portfolioAskAicaEntryIconClass, usePortfolioAskAicaEntry } from '../../../common/PortfolioAskAicaEntry/usePortfolioAskAicaEntry';

const MANAGE_LENDERS_HEADING = 'Manage Lenders';

type ManageUserProps = {
    isOrgDrawer?: boolean;
    selectedUsers?: any[];
    parentCategory?: string | null;
    selectedOrgName?: string;
    onUsersUpdate?: (updatedUsers: any[]) => void;
};

const ManageUser = ({ isOrgDrawer = false, selectedUsers, parentCategory, selectedOrgName, onUsersUpdate }: ManageUserProps) => {
    const dispatch = useAppDispatch();
    const {
        pageNo,
        params,
        showInviteTeammateModal,
        headers,
        rows,
        hasNextPage,
        showAddUserModal,
        currentRowUser,
        isCompanySide,
        uploadDataJourneyData,
        isRowsLoading,
        isHeadersLoading,
        appliedFilters,
        isFilterApplied,
        lenderCompanyCounts,
        setRows,
        setPageNo,
        setParams,
        setShowInviteTeammateModal,
        setShowAddUserModal,
        setCurrentRowUser,
        fetchRows,
        fetchDynamicHeaders,
        getTotalPages,
        getActionMenuItems,
        setSearchQuery,
        getActionConfig,
        getModalHeading,
        setAppliedFilters,
        setIsFilterApplied,
    } = useManageUser(isOrgDrawer, selectedUsers, parentCategory);
    const isMobile = useIsMobile();

    // Manage Lenders / Manage Partners is another page behind the same mobile
    // floating bar as Leads/Deals, so it gets its own instance of the shared
    // org-less Ask AICA launcher.
    const isPortfolioAskAicaEnabled = usePortfolioAskAicaEnabled();
    const { entryButton: askAicaOrbButton, chat: askAicaChat } = usePortfolioAskAicaEntry({
        enabled: isPortfolioAskAicaEnabled,
        testId: 'manage-user-ask-aica-entry',
        floating: true,
        content: <img src={chatbotGif} alt="" className={portfolioAskAicaEntryIconClass} />,
    });

    const { PARTNER, INVESTOR, LENDER } = MANAGE_USERS_TABS;

    const goToPage = (nextPage: number) => {
        setPageNo(nextPage);
        params.set('page', String(nextPage));
        setParams(params);
    };

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        goToPage(1);
    };

    const handleFilterApply = (filters: any) => {
        // For DataTable, filters come directly as key-value pairs
        const filterKeys = Object.keys(filters || {}).filter((key) => {
            const value = filters[key];
            return value && (Array.isArray(value) ? value.length > 0 : true);
        });

        // Determine the filter source key based on category
        const category = params.get('category')?.toString();
        const filterSourceKey = category === 'Investors' ? 'MANAGE_LENDERS' : 'MANAGE_PARTNERS';

        // Update Redux store with the applied filters
        dispatch(
            setCustomFilterDrawer({
                open: false, // Keep drawer closed after applying
                appliedFilters: {
                    [filterSourceKey]: filters,
                },
            }),
        );

        setAppliedFilters(filters);
        setIsFilterApplied(filterKeys.length > 0);
        goToPage(1);
    };

    // Applying an empty filter set is exactly what clearing means — same dispatch, same reset.
    const clearFilters = () => handleFilterApply({});

    const openFilterDrawer = () => dispatch(setCustomFilterDrawer({ open: true }));

    const renderHeader = () => {
        switch (true) {
            case isCompanySide: {
                const category = params.get('category')?.toString();
                const headingText = category === 'Assessee' ? 'Manage Team' : `Manage Users`;
                return (
                    <div className={styles.HeaderContainerForCompanySide}>
                        <h2 className={styles.HeadingTextForCompany}>{headingText}</h2>
                        <div className={styles.CompanyName}>{selectedOrgName || uploadDataJourneyData?.organizationName}</div>
                    </div>
                );
            }
            case !isCompanySide: {
                const category = params.get('category')?.toString();
                const showSearch = category === PARTNER || category === INVESTOR;
                const headingText = category === INVESTOR ? MANAGE_LENDERS_HEADING : `Manage ${category}`;

                return (
                    <div className={styles.HeaderContainer}>
                        <div className={styles.LeftSection}>
                            <h2 className={styles.HeadingText}>{headingText}</h2>
                        </div>
                        <div className={styles.RightSection}>
                            {showSearch && (
                                <div className={styles.SearchContainer}>
                                    <SearchBox
                                        onSearch={handleSearch}
                                        categoryParam={category || ''}
                                        location={window.location}
                                        accessTierParam=""
                                        placeholder={`Search by ${category === INVESTOR ? LENDER : PARTNER} Name (Min 3 char)`}
                                    />
                                </div>
                            )}
                            {(params.get('category')?.toString() === 'Investors' || params.get('category')?.toString() === 'Partners') && (
                                <Button
                                    text={''}
                                    onClick={openFilterDrawer}
                                    icon={debtProdFilters}
                                    style={{
                                        marginTop: 0,
                                        ...(!isFilterApplied
                                            ? {}
                                            : {
                                                  background: '#F2E8FF',
                                                  border: '1px solid #F2E8FF',
                                              }),
                                    }}
                                    variant={'secondary'}
                                    endIcon={isFilterApplied ? <CrossIcon /> : null}
                                    // Not SQUARE_END_ICON_STYLE: a JSX endIcon renders in a span
                                    // and CrossIcon carries its own 12x12, so nothing stretches.
                                    endIconStyles={{ height: '0.8rem', marginLeft: '0.25rem' }}
                                    iconStyles={
                                        isFilterApplied
                                            ? { paddingRight: '0.5rem', borderRight: '1px solid #C7C7C7', width: '1.5rem' }
                                            : { margin: '0' }
                                    }
                                    endIconClick={clearFilters}
                                />
                            )}

                            <Button
                                style={{ margin: 0 }}
                                icon={plusWhite}
                                text={getModalHeading(params.get('category')?.toString())}
                                onClick={() => {
                                    setShowInviteTeammateModal(true);
                                }}
                                variant={'purple'}
                            />
                        </div>
                    </div>
                );
            }
        }
    };

    const renderTable = () => {
        if (isHeadersLoading) {
            return (
                <div className={styles.LoadingContainer}>
                    <img src={aicaLoader} alt="Loading..." width={'80px'} />
                </div>
            );
        }

        if (isCompanySide) {
            return (
                <ManageUserTable
                    setShowAddUserModal={setShowAddUserModal}
                    setCurrentRowUser={setCurrentRowUser}
                    headers={headers}
                    rows={rows}
                    isRowsLoading={isRowsLoading}
                    isCompanySide={isCompanySide}
                    onUsersUpdate={onUsersUpdate ?? setRows}
                />
            );
        }

        const category = params.get('category')?.toString();
        if ((category === 'Partners' || category === 'Investors') && !isCompanySide) {
            return (
                <OrgBasedUsersTable
                    headers={headers}
                    rows={rows}
                    setRows={setRows}
                    isRowsLoading={isRowsLoading}
                    isSummonUser={!isNil(sessionStorage.getItem('auth_token'))}
                    actionMenuItems={getActionMenuItems()}
                    category={category === PARTNER ? 'Partner' : 'Lender'}
                    actionConfig={getActionConfig(category)}
                    fetchRows={fetchRows}
                    onFiltersApply={handleFilterApply}
                    lenderCompanyCounts={lenderCompanyCounts}
                />
            );
        } else {
            return (
                <ManageUserTable
                    setShowAddUserModal={setShowAddUserModal}
                    setCurrentRowUser={setCurrentRowUser}
                    headers={headers}
                    rows={rows}
                    isRowsLoading={isRowsLoading}
                    isCompanySide={isCompanySide}
                    onUsersUpdate={onUsersUpdate ?? setRows}
                />
            );
        }
    };

    // Mobile Manage Lenders / Manage Partners: the Leads card list in place of the DataTable.
    // Both categories share the getOrgBasedUsers row shape, so one branch serves them and only
    // the category-derived labels and config differ.
    const mobileCategory = params.get('category')?.toString();
    if (isMobile && !isCompanySide && (mobileCategory === INVESTOR || mobileCategory === PARTNER)) {
        const isLenderCategory = mobileCategory === INVESTOR;
        const orgCategory = isLenderCategory ? 'Lender' : 'Partner';
        const addHeading = getModalHeading(mobileCategory);

        return (
            <div className={styles.MobileManageOrgUsers}>
                <MobileHeader />
                <MobilePageHeader
                    title={isLenderCategory ? MANAGE_LENDERS_HEADING : `Manage ${mobileCategory}`}
                    actions={
                        <>
                            {/* Filter before search, as Leads reads. It also halves the hop the
                                collapsed square makes when the open input takes the row over and
                                hands it back: only the siblings to its right are hidden out from
                                under it, and here that is the add button alone. */}
                            {/* No activeLabel: the applied state is the funnel + ✕ pill Leads
                                shows. Both screens share the trigger and must read alike. */}
                            <FilterTriggerButton hasActiveFilters={isFilterApplied} onOpen={openFilterDrawer} onClear={clearFilters} />
                            <SearchBox
                                onSearch={handleSearch}
                                categoryParam={mobileCategory}
                                location={window.location}
                                accessTierParam=""
                                placeholder={`Search by ${isLenderCategory ? LENDER : PARTNER} Name (Min 3 char)`}
                            />
                            {/* Icon-only, so aria-label is the accessible name. text="" is not
                                optional: Button defaults an absent text to the word 'Button'. */}
                            <Button
                                className={styles.MobileAddButton}
                                icon={plusWhite}
                                text=""
                                aria-label={addHeading}
                                onClick={() => setShowInviteTeammateModal(true)}
                                variant={'purple'}
                            />
                        </>
                    }
                />
                <OrgCardList
                    // useManageUser's Rows union spans team users and org rows; the category
                    // guard above is what narrows it to the getOrgBasedUsers shape.
                    rows={rows as OrgBasedUser[]}
                    category={orgCategory}
                    loading={isRowsLoading}
                    emptyState={<div>No data found</div>}
                    hasNextPage={hasNextPage}
                    currentPageNumber={pageNo}
                    // State only, unlike the desktop pager's goToPage: infinite scroll
                    // accumulates pages in the list, so a ?page=N in the URL would reload
                    // straight into page N with everything before it unreachable.
                    onLoadMore={() => setPageNo(pageNo + 1)}
                />
                {/* Hosts the shared filter drawer and nothing else. The drawer is position:fixed,
                    so it needs no wrapper, and this renders null while it is closed. */}
                <OrgBasedUsersTable
                    filterDrawerOnly
                    rows={[]}
                    isRowsLoading={false}
                    isSummonUser={false}
                    actionMenuItems={{}}
                    actionConfig={[]}
                    headers={headers}
                    setRows={setRows}
                    category={orgCategory}
                    fetchRows={fetchRows}
                    onFiltersApply={handleFilterApply}
                />
                <InviteTeammateModal
                    isOpen={showInviteTeammateModal}
                    onClose={() => setShowInviteTeammateModal(false)}
                    fetchRows={fetchRows}
                    pageNo={pageNo}
                    getDynamicHeaders={fetchDynamicHeaders}
                    heading={addHeading}
                />

                <MobileBottomTabs trailingAction={askAicaOrbButton} />
                {askAicaChat}
            </div>
        );
    }

    return (
        <div className={!isCompanySide ? styles.ManageUsers : styles.ManageUserForCompanySide}>
            {renderHeader()}
            <div
                className={clsx(styles.TableContainer, {
                    [styles.TableContainerCompanySide]: isCompanySide,
                })}
            >
                {renderTable()}
            </div>
            {!isCompanySide && (
                <>
                    <div className={styles.PaginationContainer}>
                        <div className={styles.PaginationText}>
                            {isRowsLoading ? 'Loading...' : `Page ${pageNo} of ${getTotalPages(params.get('category')) || 1}`}
                        </div>
                        <div className={styles.ButtonContainer}>
                            <div
                                className={clsx({
                                    [styles.Disabled]: isRowsLoading || pageNo === 1,
                                    [styles.Btn]: true,
                                })}
                                onClick={() => {
                                    if (isRowsLoading || pageNo === 1) return;
                                    goToPage(pageNo - 1);
                                }}
                            >
                                Previous
                            </div>
                            <div
                                className={clsx({
                                    [styles.Btn]: true,
                                    [styles.Disabled]: isRowsLoading || !hasNextPage,
                                })}
                                onClick={() => {
                                    if (isRowsLoading || !hasNextPage) return;
                                    goToPage(pageNo + 1);
                                }}
                            >
                                Next
                            </div>
                        </div>
                    </div>
                    <AddUserModal
                        showAddUserModal={showAddUserModal}
                        setShowAddUserModal={setShowAddUserModal}
                        currentRowUser={currentRowUser}
                        onClose={() => setShowAddUserModal(false)}
                        fetchRows={fetchRows}
                        pageNo={pageNo}
                        getDynamicHeaders={fetchDynamicHeaders}
                    />
                    <InviteTeammateModal
                        isOpen={showInviteTeammateModal}
                        onClose={() => setShowInviteTeammateModal(false)}
                        fetchRows={fetchRows}
                        pageNo={pageNo}
                        getDynamicHeaders={fetchDynamicHeaders}
                        heading={getModalHeading(params.get('category')?.toString())}
                    />
                </>
            )}
        </div>
    );
};

export default ManageUser;
